/* eslint-disable no-var */
// Brand-page copy, prices and structured data, built from one live payload.
//
// Shared by the React brand page (src/Pages/PlanDetail) and the build-time
// prerender (scripts/prerender-meta.js), so the first response a crawler reads
// and the page a visitor sees say the same thing: the title, the description,
// the plan table, the FAQ and the JSON-LD all come from the same plans.
//
// CommonJS on purpose: Node requires it directly at build time, and webpack
// and Jest both accept it from the app.
//
// Only fields the brand page already shows a consumer ever leave this file.
// `sanitizeBrandPayload` is an allowlist; anything not named there (the
// catalogue's internal `cost`, Apple pricing, coin reserves, supplier or
// vendor records, GST details, stock counts) is dropped before any copy,
// markup or seed is built. The brand's CMS marketing fields (benefits,
// overview, faq, seo_title, seo_description) are left out too: they are
// hand-written, drift from the live plans (JioHotstar's still say
// "Disney+ Hotstar", Sony LIV's quote retired prices), and every claim here
// has to follow from the plans.

var currencies = require('../utils/currency');

var SITE_URL = 'https://web.getfleek.app';

// What people type into Google when it differs from the catalogue's name for
// the brand, keyed by brand slug. The catalogue name stays (plans and brands
// are never renamed); search surfaces carry both, so one page answers both
// searches: "ZEE5 (Z5) subscription".
//
// A plain string goes first ("ZEE5 (Z5)": the catalogue name is the one people
// don't search); { name, after: true } goes second ("JioHotstar (Hotstar)":
// the catalogue name is the brand's own, the old one is still searched).
var SEARCH_NAMES = {
  z5: 'ZEE5',
  jiohotstar: { name: 'Hotstar', after: true },
};

var searchNameFor = function (slug, name) {
  var entry = SEARCH_NAMES[slug];
  var alias = entry && typeof entry === 'object' ? entry.name : entry;
  return alias && alias.toLowerCase() !== name.toLowerCase() ? alias : '';
};

// "ZEE5 (Z5)", "JioHotstar (Hotstar)", or just the catalogue name.
var seoNameFor = function (slug, name) {
  var alias = searchNameFor(slug, name);
  if (!alias) return name;
  var entry = SEARCH_NAMES[slug];
  return entry && typeof entry === 'object' && entry.after ? name + ' (' + alias + ')' : alias + ' (' + name + ')';
};

// What the thing is called in a title: a gift card is not a subscription,
// and neither is anything in Gaming (top-ups, keys, console and store cards).
var nounFor = function (category) {
  var c = String(category || '');
  return /gift/i.test(c) || /^gaming$/i.test(c.trim()) ? 'gift card' : 'subscription';
};

// "the US", "the UK", "the UAE": the short form searchers type, for titles.
// Headings and copy keep the full name (placeName).
var SHORT_PLACE = { US: 'the US', GB: 'the UK', AE: 'the UAE' };
var shortPlaceName = function (country) {
  var code = String(country || '').trim().toUpperCase();
  return SHORT_PLACE[code] || placeName(code);
};

// "an OSN+ subscription", "an Xbox gift card", "a Notion subscription".
var withArticle = function (noun) {
  return (/^[aeiou]|^x/i.test(noun) ? 'an ' : 'a ') + noun;
};

// A store's own brand pages live under its country code: /us/brand/steam is
// the United States store's Steam page, /brand/steam India's (the default).
// src/seo/country-stores.json lists the countries that have them.
var countryPath = function (country) {
  var code = String(country || '').trim().toLowerCase();
  return /^[a-z]{2}$/.test(code) && code !== 'in' ? '/' + code : '';
};

// "the United States", "Germany": how a country reads after "in".
var WITH_THE = { US: 1, GB: 1, AE: 1, NL: 1, PH: 1 };
var placeName = function (country) {
  var code = String(country || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  var name = code;
  try {
    name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch (e) {
    /* a runtime without Intl.DisplayNames: the code */
  }
  return (WITH_THE[code] ? 'the ' : '') + name;
};

// Plan fields the brand page renders, and nothing else.
var PLAN_FIELDS = [
  'subscription_plan_id',
  'plan_name',
  'duration',
  'duration_type',
  'currency_type',
  'discounted_cost',
  'mrp_price',
  'new_discount_percentage',
  'coin_discount_percentage',
  'is_out_of_stock',
  'activation_type_message',
  // Sold on the brand's own site (backend marketplace.affiliate): the page
  // sends the member there instead of the cart. brand_price is the brand's
  // own price and currency, { amount, currency }.
  'affiliate',
  'brand_price',
];

var toNumber = function (value) {
  if (value === null || value === undefined || value === '') return null;
  var n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

var pick = function (obj, keys) {
  var out = {};
  keys.forEach(function (k) {
    if (obj && obj[k] !== undefined && obj[k] !== null) out[k] = obj[k];
  });
  return out;
};

/**
 * A plan bought on the brand's own site is shown at the brand's own price, in
 * the brand's own currency (brand_price), in every store - the India store too:
 * a converted figure would be a price Fleek does not set and the brand does not
 * charge. One rule for the plan rows, the price bar and the structured data
 * (2026-10-31, matching Android 10.5). Any other plan is returned unchanged.
 */
var atBrandPrice = function (p) {
  var own = p && p.affiliate === true ? p.brand_price : null;
  if (!own || own.amount === undefined || own.amount === null || own.amount === '' || !own.currency) return p;
  var out = {};
  Object.keys(p).forEach(function (k) { out[k] = p[k]; });
  out.discounted_cost = own.amount;
  out.mrp_price = own.amount;
  out.currency_type = String(own.currency).toLowerCase();
  return out;
};

/**
 * Reduce a get_digital_plans_from_vendor payload to what the brand page shows.
 * The result is safe to embed in HTML.
 */
var sanitizeBrandPayload = function (payload) {
  var data = (payload && (payload.data || payload)) || {};
  var vendor = data.vendor_data || {};
  var service = vendor.service || {};
  var plans = Array.isArray(data.plans_data) ? data.plans_data : Array.isArray(data.plans) ? data.plans : [];
  return {
    vendor_data: {
      vendor_id: vendor.vendor_id,
      description: typeof vendor.description === 'string' ? vendor.description : '',
      detail_page_banner_url: vendor.detail_page_banner_url || '',
      show_age_restrictions_popup: vendor.show_age_restrictions_popup === true,
      extra_details_required: Array.isArray(vendor.extra_details_required)
        ? vendor.extra_details_required.map(function (f) {
            return pick(f, ['json_key', 'label', 'title', 'type', 'placeholder']);
          })
        : null,
      service: pick(service, ['service_name', 'category', 'logo_url']),
    },
    plans_data: plans.map(function (p) {
      return atBrandPrice(pick(p, PLAN_FIELDS));
    }),
  };
};

// The plan's own currency, ISO-coded ("INR", "USD", "EUR"), and its prices as
// the plan rows show them: rupees in whole numbers ("₹1,199"), anything else
// to the cent unless whole ("$175.99", "$240", "€9.50"). See utils/currency.js.
var currencyCode = currencies.currencyCode;
var money = currencies.formatMoney;

// "1 month", "3 months", "12 months", "1 year".
var durationLabel = function (duration, type) {
  var d = toNumber(duration);
  var t = String(type || '').trim().toLowerCase();
  if (!d || !t) return '';
  var singular = t.replace(/s$/, '');
  return d + ' ' + (d === 1 ? singular : singular + 's');
};

// Percentage below list price, rounded as the plan rows round it.
// Zero when there is no list price or the price is not below it.
var discountPct = function (price, mrp) {
  if (!(price >= 0) || !(mrp > 0) || !(mrp > price)) return 0;
  return Math.round((1 - price / mrp) * 100);
};

/**
 * Plans in display order with the numbers every surface uses.
 * Plans outside India (any currency but rupees) that are out of stock are
 * hidden outright, exactly as the app hides them.
 */
var normalisePlans = function (plansData) {
  return (plansData || [])
    .filter(function (p) {
      return !(!currencies.isInr(p.currency_type) && p.is_out_of_stock);
    })
    .map(function (p) {
      var price = toNumber(p.discounted_cost);
      var mrp = toNumber(p.mrp_price);
      var pct = price == null ? 0 : discountPct(price, mrp);
      return {
        id: p.subscription_plan_id,
        name: String(p.plan_name || '').trim(),
        duration: durationLabel(p.duration, p.duration_type),
        currency: currencyCode(p.currency_type),
        currencyType: p.currency_type,
        price: price,
        mrp: mrp != null && price != null && mrp > price ? mrp : null,
        saving: mrp != null && price != null && mrp > price ? mrp - price : 0,
        pct: pct,
        inStock: !p.is_out_of_stock,
        activation: typeof p.activation_type_message === 'string' ? p.activation_type_message.trim() : '',
        affiliate: p.affiliate === true,
      };
    })
    .filter(function (p) {
      return p.name && p.price != null;
    });
};

// A brand sold on its own site (an affiliate plan): Fleek takes no payment
// and delivers nothing, so none of the steps above apply.
var AFFILIATE_STEPS = function (name) {
  return ['Choose a plan', 'Continue to ' + name + "'s own site", 'Sign up and pay ' + name + ' there'];
};

var HOW_IT_WORKS = function (name, abroad) {
  // The same three steps the brand page shows under "How it works".
  //
  // Delivery is deliberately not pinned to one method: the same brand can be
  // a code to redeem one day and a direct activation the next, depending on
  // how the plan is fulfilled at the time. Static pages are built once per
  // deploy, so they say both; the live page adds the chosen plan's own line.
  //
  // Outside India (any currency but rupees) checkout is card only, through Stripe, and
  // ops fulfil by hand within 24 hours by email (backend market.py
  // DELIVERY_MESSAGE): no UPI, no wallets, no WhatsApp.
  if (abroad) {
    return [
      'Pay by card',
      'Get your plan details by email within 24 hours',
      'Start using ' + name,
    ];
  }
  return [
    'Pay by UPI, card or wallet',
    'Get a code by email + WhatsApp, or have it activated on your account, depending on the plan',
    'Start using ' + name,
  ];
};

// The marketplace in a country's store: that store's home page (/us).
var marketplacePath = function (country) {
  var prefix = countryPath(country);
  return prefix || '/marketplace';
};

var planPhrase = function (p) {
  return p.name + (p.duration && p.name.toLowerCase().indexOf(p.duration) === -1 ? ' (' + p.duration + ')' : '');
};

var unique = function (list) {
  return list.filter(function (v, i) {
    return v && list.indexOf(v) === i;
  });
};

/**
 * Everything a brand page says about itself, derived from one sanitized payload.
 * Returns null when the payload does not describe a brand.
 */
var buildBrandPage = function (slug, sanitized, options) {
  var opts = options || {};
  var siteUrl = (opts.siteUrl || SITE_URL).replace(/\/$/, '');
  var vendor = (sanitized && sanitized.vendor_data) || {};
  var service = vendor.service || {};
  var name = String(service.service_name || '').trim();
  if (!name) return null;

  // A country's own page (opts.country, e.g. "US"): its own address, and the
  // country named in the title, heading and description, so each store's page
  // is a page of its own rather than a copy of another's. India's has neither.
  var prefix = countryPath(opts.country);
  var place = prefix ? placeName(opts.country) : '';
  var inPlace = place ? ' in ' + place : '';
  var url = siteUrl + prefix + '/brand/' + slug;
  var plans = normalisePlans(sanitized.plans_data);
  var paid = plans.filter(function (p) {
    return p.price > 0;
  });
  var buyable = paid.filter(function (p) {
    return p.inStock;
  });
  // Offers describe what can be bought. When nothing can, they describe what
  // is listed, marked out of stock.
  var offerPlans = buyable.length ? buyable : paid;
  var currency = offerPlans.length ? offerPlans[0].currency : 'INR';
  // Every store outside India sells in dollars, or in its own currency once
  // the backend switches it (LOCAL_CURRENCY_STORES); India sells in rupees.
  var abroad = currency !== 'INR';
  var priced = offerPlans.filter(function (p) {
    return p.currency === currency;
  });
  var prices = priced.map(function (p) {
    return p.price;
  });
  var low = prices.length ? Math.min.apply(null, prices) : null;
  var high = prices.length ? Math.max.apply(null, prices) : null;
  var cheapest = priced.filter(function (p) {
    return p.price === low;
  })[0];

  // Every plan someone can pick is bought on the brand's own site: the page
  // compares plans and hands over, and claims nothing about paying Fleek,
  // delivery, refunds or a saving.
  var affiliate = offerPlans.length > 0 && offerPlans.every(function (p) {
    return p.affiliate;
  });

  // A discount is only claimed when a plan someone can buy right now carries it.
  var best = buyable.reduce(function (acc, p) {
    return !acc || p.pct > acc.pct ? p : acc;
  }, null);
  var maxPct = best && !affiliate ? best.pct : 0;

  var searchName = searchNameFor(slug, name);
  // The brand as search surfaces name it: "ZEE5 (Z5)", or just the catalogue name.
  var seoName = seoNameFor(slug, name);
  var noun = nounFor(service.category);

  // A country's page answers what people search there: "OSN+ subscription
  // price UAE", "ElevenLabs discount". So its title says what it is, where,
  // the starting price and the best discount. India's titles are unchanged.
  var shortPlace = place ? shortPlaceName(opts.country) : '';
  var title = place
    ? buyable.length
      ? seoName + ' ' + noun + ' in ' + shortPlace + ': from ' + money(low, cheapest.currencyType) +
        (maxPct > 0 ? ', up to ' + maxPct + '% off' : '') + ' | Fleek'
      : 'Buy ' + withArticle(seoName + ' ' + noun) + ' in ' + shortPlace + ' | Fleek'
    : maxPct > 0
      ? seoName + ' subscription — up to ' + maxPct + '% off | Fleek'
      : 'Buy ' + seoName + ' subscription | Fleek';

  var description;
  if (affiliate && buyable.length) {
    description =
      'Compare ' + seoName + ' plans on Fleek, from ' + money(low, cheapest.currencyType) + ', and buy on ' + name + "'s own site. " +
      buyable.length + (buyable.length === 1 ? ' plan.' : ' plans.');
  } else if (buyable.length) {
    description =
      (place ? 'Buy ' + withArticle(seoName + ' ' + noun) + inPlace : 'Buy ' + seoName) + ' on Fleek from ' + money(low, cheapest.currencyType) +
      (maxPct > 0 ? ', up to ' + maxPct + '% below the list price' : '') +
      '. ' + buyable.length + (buyable.length === 1 ? ' plan' : ' plans') +
      (abroad ? ', paid by card, delivered by email within 24 hours.' : ', paid by UPI, card or wallet, delivered by email and WhatsApp.');
  } else if (plans.length) {
    description = seoName + ' plans on Fleek are currently unavailable. See the plans and prices we list and browse other subscriptions on Fleek.';
  } else {
    description = 'Buy ' + seoName + inPlace + ' and other subscriptions on Fleek, paid by ' + (abroad ? 'card.' : 'UPI, card or wallet.');
  }

  var activations = unique(
    buyable.map(function (p) {
      return p.activation;
    })
  );
  var steps = affiliate ? AFFILIATE_STEPS(name) : HOW_IT_WORKS(name, abroad);

  var faq = [];
  if (affiliate && buyable.length) {
    faq.push({
      question: 'How much does ' + seoName + ' cost?',
      answer:
        name + ' starts at ' + money(low, cheapest.currencyType) + ' for ' + planPhrase(cheapest) + '. Plans: ' +
        buyable
          .map(function (p) {
            return planPhrase(p) + ' ' + money(p.price, p.currencyType);
          })
          .join('; ') +
        '. You buy them on ' + name + "'s own site.",
    });
  } else if (buyable.length) {
    faq.push({
      question: 'How much does ' + seoName + ' cost on Fleek?',
      answer:
        name + ' on Fleek starts at ' + money(low, cheapest.currencyType) + ' for ' + planPhrase(cheapest) + '. Plans available now: ' +
        buyable
          .map(function (p) {
            return planPhrase(p) + ' ' + money(p.price, p.currencyType) + (p.mrp ? ' (list price ' + money(p.mrp, p.currencyType) + ')' : '');
          })
          .join('; ') +
        '.',
    });
  } else if (plans.length) {
    faq.push({
      question: 'Can I buy ' + name + ' on Fleek right now?',
      answer:
        'Not at the moment: every ' + name + ' plan on Fleek is currently unavailable. Listed plans: ' +
        plans
          .map(function (p) {
            return planPhrase(p) + ' ' + money(p.price, p.currencyType);
          })
          .join('; ') +
        '.',
    });
  }
  if (maxPct > 0) {
    faq.push({
      question: 'How much do I save on ' + name + ' with Fleek?',
      answer:
        'Up to ' + maxPct + '% off the list price. ' + planPhrase(best) + ' costs ' + money(best.price, best.currencyType) +
        ' on Fleek against a list price of ' + money(best.mrp, best.currencyType) + ', a saving of ' + money(best.saving, best.currencyType) + '.',
    });
  }
  // What searchers abroad ask before buying from a store they don't know.
  // India's store has coupons and its own FAQ, so these are for the
  // stores outside India only.
  if (abroad && buyable.length && !affiliate) {
    if (maxPct > 0) {
      faq.push({
        question: 'Do I need ' + withArticle(name + ' discount code') + '?',
        answer:
          'No. The price on Fleek is already up to ' + maxPct + '% below ' + name + "'s list price, with nothing to enter at checkout.",
      });
    }
    faq.push({
      question: 'Is this an official ' + name + ' ' + noun + '?',
      answer:
        'Yes. Every plan on Fleek is sourced through an authorised partnership with the brand or its distributor, and you get a genuine ' +
        name + ' ' + noun + (noun === 'subscription' ? ' on your own account' : '') +
        '. If a plan cannot be activated, we refund you in full.',
    });
    if (place) {
      faq.push({
        question: 'Can I buy ' + name + ' from ' + place + '?',
        answer: 'Yes. This is Fleek\'s store for ' + place + ': prices are in ' + currency + ' and you pay by card.',
      });
    }
  }
  if (affiliate) {
    faq.push({
      question: 'How do I buy ' + name + ' from Fleek?',
      answer:
        'Choose a plan on Fleek and continue to ' + name + "'s own site, where you sign up and pay " + name +
        ' directly. ' + name + ' runs your account and billing.',
    });
  } else {
    faq.push({
      question: 'How do I get my ' + name + ' ' + noun + ' after paying?',
      answer:
        steps.join('. ').replace(' + ', ' and ') + '. The plan you choose shows how it is delivered before you pay.',
    });
  }

  var image = vendor.detail_page_banner_url || service.logo_url || '';

  var offers = null;
  if (priced.length) {
    var availability = buyable.length ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
    var single = function (p) {
      return {
        '@type': 'Offer',
        name: p.name,
        price: p.price,
        priceCurrency: p.currency,
        availability: p.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: url,
        // An affiliate plan is sold by the brand, on its own site.
        seller: { '@type': 'Organization', name: affiliate ? name : 'Fleek' },
      };
    };
    offers =
      priced.length === 1
        ? single(priced[0])
        : {
            '@type': 'AggregateOffer',
            lowPrice: low,
            highPrice: high,
            offerCount: priced.length,
            priceCurrency: currency,
            availability: availability,
            url: url,
            offers: priced.map(single),
          };
  }

  var product = {
    '@type': 'Product',
    '@id': url + '#product',
    name: name,
    description: vendor.description || description,
    brand: { '@type': 'Brand', name: name },
    url: url,
  };
  if (searchName) {
    product.alternateName = searchName;
    product.brand.alternateName = searchName;
  }
  if (service.category) product.category = service.category;
  var images = unique([service.logo_url, vendor.detail_page_banner_url]);
  if (images.length) product.image = images;
  if (offers) product.offers = offers;

  var jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Marketplace', item: siteUrl + marketplacePath(opts.country) },
          { '@type': 'ListItem', position: 2, name: name, item: url },
        ],
      },
      product,
      {
        '@type': 'FAQPage',
        '@id': url + '#faq',
        mainEntity: faq.map(function (f) {
          return { '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } };
        }),
      },
    ],
  };

  return {
    slug: slug,
    url: url,
    name: name,
    searchName: searchName,
    heading: place ? seoName + ' ' + noun + inPlace : seoName + ' subscription',
    country: prefix ? String(opts.country).toUpperCase() : '',
    place: place,
    category: service.category || '',
    description: vendor.description || '',
    image: image,
    title: title,
    metaDescription: description,
    plans: plans,
    buyableCount: buyable.length,
    lowPrice: buyable.length ? low : null,
    currency: currency,
    maxPct: maxPct,
    affiliate: affiliate,
    steps: steps,
    activations: activations,
    faq: faq,
    jsonLd: jsonLd,
  };
};

module.exports = {
  SITE_URL: SITE_URL,
  PLAN_FIELDS: PLAN_FIELDS,
  sanitizeBrandPayload: sanitizeBrandPayload,
  atBrandPrice: atBrandPrice,
  normalisePlans: normalisePlans,
  buildBrandPage: buildBrandPage,
  countryPath: countryPath,
  placeName: placeName,
  marketplacePath: marketplacePath,
  discountPct: discountPct,
  durationLabel: durationLabel,
  money: money,
};
